import { useContext, useRef, useState, useEffect } from 'react'
import GridLayout, { WidthProvider, calculateUtils } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
  propsDataLocationToPathAndValue,
} from '@ea-lab/reactive-json'

/**
 * Generic 2-D dashboard grid (react-grid-layout) bound to a store array of
 * widgets (`dataLocation`). Each widget is `{ <type>: { x, y, w, h, ...data } }`.
 * Renders each widget's template (via `options[type]` → `<View load>`) inside a
 * draggable + resizable grid cell ; on drag/resize END it writes the new
 * `{x,y,w,h}` back to each widget and fires the YAML `actions:` (default event
 * `gridcommit`) so the page persists the layout declaratively.
 *
 * Why a primitive : 2-D placement + width/height resize + collision/compaction is
 * intrinsically imperative/DOM (react-grid-layout) — impossible in declarative
 * YAML. Widgets keep their content 100 % declarative (the `options` templates).
 *
 * Perf : dragging/resizing does NOT touch the store until release, so charts
 * (ChartLoader, value-stable fetch) never refetch mid-gesture. `editable` gates
 * drag/resize (Personnaliser vs Voir).
 *
 * YAML :
 *   - type: DashboardGrid
 *     dataLocation: ~~.dash.current.widgets
 *     editing: ~~.editing             # boolean — gate drag/resize (Personnaliser vs Voir)
 *     cols: 12
 *     rowHeight: 34
 *     options: { kpiRow: kpiRowWidget, revenueChart: revenueChartWidget, … }
 *     actions:
 *       - { what: submitData, on: gridcommit, url: …, … }   # submit FIRST (custom-event order)
 *       - { what: stringifyData, on: gridcommit, source: …, target: … }
 */
const Grid = WidthProvider(GridLayout)

// Taille par défaut d'un widget nouvellement ajouté (sans x/y/w/h stockés).
const TYPE_DEFAULTS = {
  kpiRow: { w: 12, h: 6 },
  revenueChart: { w: 12, h: 9 },
  pipelineCount: { w: 6, h: 13 },
  pipelineAmount: { w: 6, h: 13 },
}

const typeOf = (w) => (w && typeof w === 'object' ? Object.keys(w)[0] : '')
const num = (v, d) => (Number.isFinite(v) ? v : d)

export const DashboardGrid = ({ props, path, datafield }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const containerRef = useRef(null)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const { formData, formDataPath } = propsDataLocationToPathAndValue({
    currentPath: path,
    datafield,
    dataLocation: props.dataLocation,
    defaultValue: [],
    globalDataContext,
    templateContext,
  })
  const widgets = Array.isArray(formData) ? formData : []
  const options = resolve(props.options) || {}
  // `editing` (bool) gates drag/resize (Personnaliser vs Voir). `editable` toléré en alias.
  const editable = !!resolve(props.editing ?? props.editable)
  const cols = parseInt(resolve(props.cols), 10) || 12
  const rowHeight = parseInt(resolve(props.rowHeight), 10) || 34
  const commitEvent = resolve(props.commitEvent) || 'gridcommit'
  const MARGIN = [24, 24]

  // Largeur réelle de la grille (mesurée) → géométrie exacte des cellules « ombres »
  // affichées en mode édition (mêmes calculs que react-grid-layout).
  const [gridW, setGridW] = useState(0)
  useEffect(() => {
    const host = containerRef.current
    if (!host) return
    const el = host.querySelector('.react-grid-layout') || host
    const update = () => setGridW(el.clientWidth)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Layout react-grid-layout dérivé des widgets (défauts si coords absentes →
  // rétro-compat avec les anciens dashboards `wrapperClass`).
  const layout = widgets.map((w, i) => {
    const t = typeOf(w)
    const d = w[t] || {}
    const def = TYPE_DEFAULTS[t] || { w: 6, h: 9 }
    return {
      i: String(i),
      x: num(d.x, (i * 6) % cols),
      y: num(d.y, Math.floor(i / 2) * 10),
      w: num(d.w, def.w),
      h: num(d.h, def.h),
      minW: 2,
      minH: 2,
    }
  })

  // Cellules « ombres » (mode édition) : rectangles calculés avec la géométrie
  // native de RGL → alignement parfait avec les widgets. Pointer-events désactivés.
  const contentRows = layout.reduce((m, l) => Math.max(m, l.y + l.h), 0)
  const gridRows = editable ? Math.max(contentRows, 14) + 1 : contentRows
  const posParams = { margin: MARGIN, containerPadding: MARGIN, containerWidth: gridW, cols, rowHeight, maxRows: Infinity }
  const cells = (editable && gridW > 0)
    ? Array.from({ length: gridRows * cols }, (_, k) => {
      const x = k % cols
      const y = Math.floor(k / cols)
      return calculateUtils.calcGridItemPosition(posParams, x, y, 1, 1, {})
    })
    : []
  const editMinH = (editable && gridW > 0)
    ? calculateUtils.calcGridItemPosition(posParams, 0, gridRows - 1, 1, 1, {}).top + rowHeight + MARGIN[1]
    : undefined

  const draggingRef = useRef(false)

  // Écrit les coords RGL dans le store (SANS sauvegarde), en n'écrivant que si
  // elles ont VRAIMENT changé (anti-boucle de rendu) et jamais pendant un geste.
  const syncStore = (newLayout) => {
    const byId = {}
    newLayout.forEach((l) => { byId[l.i] = l })
    let changed = false
    const next = widgets.map((w, i) => {
      const t = typeOf(w)
      const l = byId[String(i)]
      const d = w[t] || {}
      if (!l) return w
      if (d.x !== l.x || d.y !== l.y || d.w !== l.w || d.h !== l.h) changed = true
      return { [t]: { ...w[t], x: l.x, y: l.y, w: l.w, h: l.h } }
    })
    if (changed) globalDataContext.updateData(next, formDataPath)
    return changed
  }

  // Persiste (déclare le save déclaratif) : le store porte déjà les coords finales.
  const save = () => containerRef.current?.dispatchEvent(new Event(commitEvent, { bubbles: true }))

  const onLayoutChange = (l) => { if (!draggingRef.current) syncStore(l) }
  const onDragStop = (l) => { draggingRef.current = false; syncStore(l); save() }
  const onResizeStop = (l) => { draggingRef.current = false; syncStore(l); save() }
  const startGesture = () => { draggingRef.current = true }

  return (
    <ActionDependant {...props} attributesHolderRef={containerRef}>
      <div ref={containerRef} className="relative" style={editMinH ? { minHeight: `${editMinH}px` } : undefined}>
        {cells.length > 0 && (
          <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
            {cells.map((c, i) => (
              <div
                key={i}
                className="absolute rounded-lg bg-slate-400/5 border border-dashed border-slate-300/60"
                style={{ left: c.left, top: c.top, width: c.width, height: c.height }}
              />
            ))}
          </div>
        )}
        <Grid
          className="layout relative z-10"
          layout={layout}
          cols={cols}
          rowHeight={rowHeight}
          margin={MARGIN}
          isDraggable={editable}
          isResizable={editable}
          draggableHandle=".widget-drag-handle"
          resizeHandles={['se']}
          compactType="vertical"
          onLayoutChange={onLayoutChange}
          onDragStart={startGesture}
          onResizeStart={startGesture}
          onDragStop={onDragStop}
          onResizeStop={onResizeStop}
        >
          {widgets.map((w, i) => {
            const t = typeOf(w)
            return (
              <div key={String(i)} className="card flex flex-col overflow-hidden">
                <View
                  props={{ load: options[t] }}
                  path={`${formDataPath}.${i}.${t}`}
                  datafield={String(i)}
                  currentData={w[t]}
                />
              </div>
            )
          })}
        </Grid>
      </div>
    </ActionDependant>
  )
}
